import { useCallback, useEffect, useRef, useState } from 'react';
import { DateTime, Duration } from 'luxon';
import { useIntl } from 'react-intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { getDurationSecondsFormFormat } from '../../utils';
import { useTimerContext } from './TimerContext';

export type UseTimerResult = {
  startTime: DateTime | null;
  timer: Duration;
  running: boolean;
  onPlay: () => void;
  onPause: () => void;
  onReset: () => void;
  onChange: (value: number | string | null) => void;
  today: React.MutableRefObject<DateTime>;
};

export const useTimer = ({
  duration,
  resetTimeRange,
  resetDuration,
  handleDuration,
  showStartEndTime,
  suggestionProps,
  setResetTimeValue
}: {
  duration?: number;
  resetTimeRange: () => void;
  resetDuration: () => void;
  handleDuration(duration: Duration | null, showStartEndTime?: boolean): void;
  showStartEndTime?: boolean;
  suggestionProps: {
    setHasSuggestionStartTime: React.Dispatch<React.SetStateAction<boolean>>;

    setHasSuggestionDuration: React.Dispatch<React.SetStateAction<boolean>>;

    setHasSuggestionEndTime: React.Dispatch<React.SetStateAction<boolean>>;

    hasSuggestionDuration: boolean;
  };
  setResetTimeValue?: (resetTimeValue: boolean) => void;
}): UseTimerResult => {
  const {
    running,
    startTimer,
    stopTimer,
    setStartTime: setStartTimeInContext
  } = useTimerContext();
  const intl = useIntl();
  const [startTime, setStartTime] = useState<DateTime | null>(null);
  const [timer, setTimer] = useState(Duration.fromObject({ seconds: 0 }));
  const today = useRef(DateTime.local().endOf('day'));
  const isTiwDurationIndependentStartEndTime = useFeatureFlag(
    'isTiwDurationIndependentStartEndTime'
  );

  const onPlay = useCallback(() => {
    setResetTimeValue?.(false);
    startTimer();
    const startTime: DateTime = duration
      ? DateTime.local().minus(Duration.fromObject({ seconds: duration }))
      : DateTime.local();

    setStartTimeInContext(startTime);
    setStartTime(startTime);
    setTimeout(() => {
      handleDuration(DateTime.local().diff(startTime), showStartEndTime);
    }, 1000);
    if (!isTiwDurationIndependentStartEndTime) {
      resetTimeRange();
    }
  }, [
    setResetTimeValue,
    startTimer,
    duration,
    setStartTimeInContext,
    isTiwDurationIndependentStartEndTime,
    resetTimeRange,
    handleDuration,
    showStartEndTime
  ]);

  const onPause = useCallback(() => {
    setResetTimeValue?.(false);
    if (startTime) {
      handleDuration(DateTime.local().diff(startTime), showStartEndTime);
    }

    stopTimer();
    setStartTimeInContext(null);
    setStartTime(null);

    suggestionProps.setHasSuggestionStartTime(false);

    suggestionProps.setHasSuggestionDuration(false);

    suggestionProps.setHasSuggestionEndTime(false);
  }, [
    setResetTimeValue,
    startTime,
    stopTimer,
    setStartTimeInContext,
    suggestionProps,
    handleDuration,
    showStartEndTime
  ]);

  const onReset = useCallback(() => {
    stopTimer();
    setStartTimeInContext(null);
    setStartTime(null);
    handleDuration(Duration.fromObject({ seconds: 0 }), showStartEndTime);
    setTimer(Duration.fromObject({}));
    resetTimeRange();
    resetDuration();
    setResetTimeValue?.(true);
  }, [
    stopTimer,
    setStartTimeInContext,
    handleDuration,
    showStartEndTime,
    resetTimeRange,
    resetDuration,
    setResetTimeValue
  ]);

  const onChange = useCallback(
    (value: number | string | null) => {
      if (!running) {
        setStartTime(null);
        const time =
          typeof value === 'string'
            ? getDurationSecondsFormFormat(value, intl)
            : value;

        if (time) {
          handleDuration(
            Duration.fromObject({ seconds: time }),
            showStartEndTime
          );
          suggestionProps.setHasSuggestionStartTime(false);

          suggestionProps.setHasSuggestionDuration(false);

          suggestionProps.setHasSuggestionEndTime(false);
        } else {
          if (time === null) {
            handleDuration(null, showStartEndTime);
          } else {
            handleDuration(Duration.fromObject({}), showStartEndTime);
          }
        }
      }
      if (!isTiwDurationIndependentStartEndTime) {
        resetTimeRange();
      }
    },
    [
      running,
      isTiwDurationIndependentStartEndTime,
      resetTimeRange,
      intl,
      handleDuration,
      showStartEndTime,
      suggestionProps
    ]
  );

  useEffect(() => {
    let interval: any = null;
    if (running && startTime) {
      setTimer(DateTime.local().diff(startTime, 'seconds'));
      interval = setInterval(() => {
        setTimer(DateTime.local().diff(startTime, 'seconds'));
      }, 1000);
    } else if (!running) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [handleDuration, running, showStartEndTime, startTime]);

  //if midnight crossed stop the timer automatically
  useEffect(() => {
    if (running) {
      window.dispatchEvent(new Event('timer'));
    }
    if (running && timer.seconds > 0 && DateTime.local() >= today.current) {
      onPause();
    }
  }, [startTime, running, onPause, timer]);

  return {
    startTime,
    timer,
    running,
    onPlay,
    onPause,
    onReset,
    onChange,
    today
  };
};
